/**
 * Utilities for the RTE display type on the table field.
 *
 * Merge variables use the {{columnId}} syntax. When resolved:
 *  - In column headers → replaced with the column title
 *  - In cell templates → replaced with the row's cell value for that column
 *
 * For dropdown / multiSelect columns the resolved value may carry the
 * option's backgroundColor so the field renderer can show it as a coloured
 * pill.
 */

import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

dayjs.extend(utc);
dayjs.extend(timezone);

const MERGE_VAR_RE = /\{\{(\w+)\}\}/g;

// ── Column look-ups ────────────────────────────────────────────────

const _columnMapCache = new WeakMap();

const buildColumnMap = (tableColumns) => {

  if (!tableColumns) return {};

  const cached = _columnMapCache.get(tableColumns);

  if (cached) return cached;

  const map = {};

  tableColumns.forEach((col) => { map[col._id] = col; });

  _columnMapCache.set(tableColumns, map);

  return map;

};

// ── Plain-text extraction from TipTap JSON ─────────────────────────

const extractText = (node) => {

  if (!node) return '';

  if (node.type === 'text') return node.text || '';

  if (node.content) return node.content.map(extractText).join('');

  return '';

};

// ── Shared empty-value check ────────────────────────────────────────

const isCellEmpty = (val) => {

  if (val == null || val === '') return true;

  if (Array.isArray(val) && val.length === 0) return true;

  return false;

};

// ── Resolve header merge codes ─────────────────────────────────────

/**
 * Walk the title (TipTap JSON or plain string) and replace every
 * {{columnId}} with the matching column's title.
 *
 * @param {object|string} titleJson - TipTap JSON doc or plain string
 * @param {Array}         tableColumns
 * @returns {string} resolved plain-text title
 */
export const resolveHeaderMergeCodes = (titleJson, tableColumns) => {

  const raw = typeof titleJson === 'string' ? titleJson : extractText(titleJson);

  const colMap = buildColumnMap(tableColumns);

  return raw.replace(MERGE_VAR_RE, (match, colId) => {

    const col = colMap[colId];

    return col ? (col.title || '') : '';

  });

};

/**
 * Walk the title (TipTap JSON or plain string) and replace every
 * {{columnId}} with the matching column's title, returning an array
 * of segments that preserve TipTap textStyle colour marks.
 *
 * @param {object|string} titleJson - TipTap JSON doc or plain string
 * @param {Array}         tableColumns
 * @returns {CellSegment[]}
 */
export const resolveHeaderSegments = (titleJson, tableColumns) => {

  const colMap = buildColumnMap(tableColumns);

  const segments = [];

  const walkNode = (node, headingLevel) => {

    if (!node) return;

    if (node.type === 'text') {

      const text = node.text || '';

      const marks = getTextMarks(node);

      if (headingLevel) marks.headingLevel = headingLevel;

      let lastIndex = 0;

      text.replace(MERGE_VAR_RE, (match, colId, offset) => {

        if (offset > lastIndex) {
          segments.push({ text: text.slice(lastIndex, offset), ...marks });
        }

        const col = colMap[colId];

        segments.push({ text: col ? (col.title || '') : '', ...marks });

        lastIndex = offset + match.length;

        return match;

      });

      if (lastIndex < text.length) {
        segments.push({ text: text.slice(lastIndex), ...marks });
      }

      return;

    }

    if (node.type === 'hardBreak') {

      segments.push({ isBreak: true });

      return;

    }

    const level = node.type === 'heading' ? (node.attrs?.level || 1) : headingLevel;

    if (node.content) {

      node.content.forEach((child, idx) => {

        if (idx > 0 && (child.type === 'paragraph' || child.type === 'heading')) {
          segments.push({ isBreak: true });
        }

        walkNode(child, level);

      });

    }

  };

  if (typeof titleJson === 'string') {

    let lastIndex = 0;

    titleJson.replace(MERGE_VAR_RE, (match, colId, offset) => {

      if (offset > lastIndex) {
        segments.push({ text: titleJson.slice(lastIndex, offset), textStyleColor: null });
      }

      const col = colMap[colId];

      segments.push({ text: col ? (col.title || '') : '', textStyleColor: null });

      lastIndex = offset + match.length;

      return match;

    });

    if (lastIndex < titleJson.length) {
      segments.push({ text: titleJson.slice(lastIndex), textStyleColor: null });
    }

    return segments;

  }

  walkNode(titleJson);

  return segments;

};

// ── Resolve cell merge codes ───────────────────────────────────────

/**
 * Find the option object matching a single value (either _id or display value).
 */
const findOption = (column, singleValue) => {

  if (!column || !column.options || !Array.isArray(column.options)) return null;

  for (let i = 0; i < column.options.length; i++) {

    const opt = column.options[i];

    if (opt._id === singleValue || opt.value === singleValue) return opt;

  }

  return null;

};

/**
 * Extract the textStyle color mark from a TipTap text node, if any.
 */
const getTextStyleColor = (node) => {

  if (!node || !node.marks) return null;

  for (let i = 0; i < node.marks.length; i++) {

    const mark = node.marks[i];

    if (mark.type === 'textStyle' && mark.attrs?.color) return mark.attrs.color;

  }

  return null;

};

/**
 * Extract all formatting marks from a TipTap text node.
 */
const getTextMarks = (node) => {

  const result = { bold: false, italic: false, strike: false, underline: false, textStyleColor: null };

  if (!node || !node.marks) return result;

  for (let i = 0; i < node.marks.length; i++) {

    const mark = node.marks[i];

    if (mark.type === 'bold') result.bold = true;

    else if (mark.type === 'italic') result.italic = true;

    else if (mark.type === 'strike') result.strike = true;

    else if (mark.type === 'underline') result.underline = true;

    else if (mark.type === 'textStyle' && mark.attrs?.color) result.textStyleColor = mark.attrs.color;

  }

  return result;

};

/**
 * Segment produced by resolveCellMergeCodes.
 * @typedef {Object} CellSegment
 * @property {string}  text   - display text
 * @property {boolean} isPill - should render as a coloured pill
 * @property {string|null} color - pill background colour (hex)
 * @property {string|null} textStyleColor - colour from TipTap textStyle mark
 */

/**
 * Walk the cell template (TipTap JSON), resolve merge codes for a given
 * row, and return an array of segments ready for rendering.
 *
 * @param {object|string} valueJson    - TipTap JSON doc or plain string
 * @param {object}        row          - { cells: { colId: value, ... } }
 * @param {Array}         tableColumns
 * @param {string}        mode         - 'edit' | 'fill' | 'readonly' | 'pdf'
 * @param {object}        [fieldPositionTableColumns] - { colId: { format, ... } }
 * @returns {CellSegment[]}
 */
export const resolveCellMergeCodes = (valueJson, row, tableColumns, mode, fieldPositionTableColumns) => {

  const colMap = buildColumnMap(tableColumns);

  const segments = [];

  const pushResolved = (column, cellValue, marks) => {

    const colType = column.type;

    const pillColor = marks.textStyleColor || null;

    if (colType === 'dropdown') {

      const opt = findOption(column, cellValue);

      const displayValue = opt ? (opt.value || '') : String(cellValue);

      segments.push({ text: displayValue, isPill: !!pillColor, color: pillColor, ...marks });

    } else if (colType === 'multiSelect' && Array.isArray(cellValue)) {

      cellValue.forEach((val, idx) => {

        const opt = findOption(column, val);

        const displayValue = opt ? (opt.value || '') : String(val);

        if (idx > 0) {
          segments.push({ text: ', ', isPill: false, color: null, ...marks });
        }

        segments.push({ text: displayValue, isPill: !!pillColor, color: pillColor, ...marks });

      });

    } else if (colType === 'date' && cellValue) {

      const colFormat = (fieldPositionTableColumns && fieldPositionTableColumns[column._id] && fieldPositionTableColumns[column._id].format) || column.format || 'MM/DD/YYYY';

      const tz = row?.tz;

      const displayValue = tz
        ? dayjs(cellValue).tz(tz).format(colFormat)
        : dayjs(cellValue).format(colFormat);

      segments.push({ text: displayValue, isPill: !!pillColor, color: pillColor, ...marks });

    } else if (colType === 'image') {

      const images = Array.isArray(cellValue) ? cellValue : [cellValue];

      const urls = images.map((img) => (typeof img === 'string' ? img : img?.url)).filter(Boolean);

      if (urls.length > 0) {
        segments.push({ isImage: true, urls, maxWidth: column.maxImageWidth, maxHeight: column.maxImageHeight });
      }

    } else if (colType === 'signature' && cellValue) {

      const url = typeof cellValue === 'string' ? cellValue : cellValue?.url;

      if (url) {
        segments.push({ isImage: true, urls: [url], maxWidth: column.maxImageWidth, maxHeight: column.maxImageHeight });
      }

    } else {

      const displayValue = Array.isArray(cellValue) ? cellValue.join(', ') : String(cellValue);

      segments.push({ text: displayValue, isPill: !!pillColor, color: pillColor, ...marks });

    }

  };

  const walkNode = (node, headingLevel) => {

    if (!node) return;

    if (node.type === 'text') {

      const text = node.text || '';

      const marks = getTextMarks(node);

      if (headingLevel) marks.headingLevel = headingLevel;

      let lastIndex = 0;

      text.replace(MERGE_VAR_RE, (match, colId, offset) => {

        if (offset > lastIndex) {
          segments.push({ text: text.slice(lastIndex, offset), isPill: false, color: null, ...marks });
        }

        const column = colMap[colId];

        const cellValue = row?.cells?.[colId];

        if (column && !isCellEmpty(cellValue)) {
          pushResolved(column, cellValue, marks);
        }

        lastIndex = offset + match.length;

        return match;

      });

      if (lastIndex < text.length) {
        segments.push({ text: text.slice(lastIndex), isPill: false, color: null, ...marks });
      }

      return;

    }

    if (node.type === 'hardBreak') {

      segments.push({ isBreak: true });

      return;

    }

    const level = node.type === 'heading' ? (node.attrs?.level || 1) : headingLevel;

    if (node.content) {

      node.content.forEach((child, idx) => {

        if (idx > 0 && (child.type === 'paragraph' || child.type === 'heading')) {
          segments.push({ isBreak: true });
        }

        walkNode(child, level);

      });

    }

  };

  if (typeof valueJson === 'string') {

    let lastIndex = 0;

    valueJson.replace(MERGE_VAR_RE, (match, colId, offset) => {

      if (offset > lastIndex) {
        segments.push({ text: valueJson.slice(lastIndex, offset), isPill: false, color: null, textStyleColor: null });
      }

      const column = colMap[colId];

      const cellValue = row?.cells?.[colId];

      if (column && !isCellEmpty(cellValue)) {
        pushResolved(column, cellValue, null);
      }

      lastIndex = offset + match.length;

      return match;

    });

    if (lastIndex < valueJson.length) {
      segments.push({ text: valueJson.slice(lastIndex), isPill: false, color: null, textStyleColor: null });
    }

    return segments;

  }

  walkNode(valueJson);

  return segments;

};

/**
 * Convenience wrapper: build segments and strip trailing/leading
 * whitespace from the combined result.
 */
export const buildRteCellSegments = (valueJson, row, tableColumns, mode) => {

  return resolveCellMergeCodes(valueJson, row, tableColumns, mode);

};

/**
 * Check whether a row has at least one non-empty cell value for any
 * merge variable referenced across all RTE column templates, or whether
 * any template contains plain text content (non-merge-code text).
 *
 * @param {Array}  rteColumns   - settings array from the field
 * @param {object} row          - { cells: { colId: value, ... } }
 * @param {Array}  tableColumns - full tableColumns list
 * @returns {boolean} true if the row has content for at least one referenced merge variable
 */
export const isRowPopulatedForRte = (rteColumns, row, tableColumns) => {

  if (!rteColumns) return false;

  const colMap = buildColumnMap(tableColumns);

  for (let i = 0; i < rteColumns.length; i++) {

    const template = rteColumns[i].value;

    const raw = typeof template === 'string' ? template : extractText(template);

    const plainText = raw.replace(MERGE_VAR_RE, '').trim();

    if (plainText.length > 0) return true;

    if (row?.cells) {

      let match;

      MERGE_VAR_RE.lastIndex = 0;

      while ((match = MERGE_VAR_RE.exec(raw)) !== null) {

        const colId = match[1];

        if (colMap[colId] && !isCellEmpty(row.cells[colId])) {
          return true;
        }

      }

    }

  }

  return false;

};

/**
 * Keep only visible RTE items in the virtual scroll range. Measuring thousands of hidden
 * rows at zero height repeatedly expands that range and can exceed React's update limit.
 * Collection items carry their own schema/template; filter each item independently so a
 * hidden parent row does not hide populated children. Keep the original row objects/indices.
 * Builder and PDF measurement paths must retain their unfiltered items.
 */
export const getVisibleRteItems = (items, settings, tableColumns) => items.filter((item) => {

  const rteColumns = item.fieldPositionSchema?.settings || settings;

  if (item.type === 'header') return !!rteColumns?.length;
  if (item.type === 'row') return isRowPopulatedForRte(rteColumns, item, item.schema?.tableColumns || tableColumns);

  return true;

});

/**
 * Check whether a single cell template has renderable content for the
 * given row — either plain text or at least one merge variable that
 * resolves to a non-empty value.
 */
export const templateHasContent = (templateJson, row, tableColumns) => {

  const raw = typeof templateJson === 'string' ? templateJson : extractText(templateJson);

  const plainText = raw.replace(MERGE_VAR_RE, '').trim();

  if (plainText.length > 0) return true;

  if (!row?.cells) return false;

  const colMap = buildColumnMap(tableColumns);

  MERGE_VAR_RE.lastIndex = 0;

  let match;

  while ((match = MERGE_VAR_RE.exec(raw)) !== null) {

    const colId = match[1];

    if (colMap[colId] && !isCellEmpty(row.cells[colId])) {
      return true;
    }

  }

  return false;

};

/**
 * Extract unique column IDs referenced by merge codes in a TipTap JSON
 * template (or plain string).  Returns an array of column ID strings
 * in the order they first appear.
 *
 * @param {object|string} templateJson - TipTap JSON doc or plain string
 * @returns {string[]}
 */
export const extractMergeCodeColumnIds = (templateJson) => {

  const raw = typeof templateJson === 'string' ? templateJson : extractText(templateJson);

  const seen = {};

  const ids = [];

  MERGE_VAR_RE.lastIndex = 0;

  let match;

  while ((match = MERGE_VAR_RE.exec(raw)) !== null) {

    const colId = match[1];

    if (!seen[colId]) {
      seen[colId] = true;
      ids.push(colId);
    }

  }

  return ids;

};

/**
 * Walk TipTap JSON and produce an array of display segments preserving
 * all formatting marks — without resolving merge codes.  Intended for
 * static (non-editor) rendering of raw template content.
 *
 * @param {object|string} json - TipTap JSON doc or plain string
 * @returns {CellSegment[]}
 */
export const tiptapJsonToSegments = (json) => {

  if (!json) return [];

  if (typeof json === 'string') {
    return json ? [{ text: json }] : [];
  }

  const segments = [];

  const walkNode = (node, headingLevel) => {

    if (!node) return;

    if (node.type === 'text') {

      const text = node.text || '';

      const marks = getTextMarks(node);

      if (headingLevel) marks.headingLevel = headingLevel;

      segments.push({ text, ...marks });

      return;

    }

    if (node.type === 'hardBreak') {

      segments.push({ isBreak: true });

      return;

    }

    const level = node.type === 'heading' ? (node.attrs?.level || 1) : headingLevel;

    if (node.content) {

      node.content.forEach((child, idx) => {

        if (idx > 0 && (child.type === 'paragraph' || child.type === 'heading')) {
          segments.push({ isBreak: true });
        }

        walkNode(child, level);

      });

    }

  };

  walkNode(json);

  return segments;

};

export default {
  resolveHeaderMergeCodes,
  resolveHeaderSegments,
  resolveCellMergeCodes,
  buildRteCellSegments,
  isRowPopulatedForRte,
  templateHasContent,
  extractMergeCodeColumnIds,
  tiptapJsonToSegments,
};
